import pageTitle from 'ember-page-title/helpers/page-title';
import PageSetup from 'bcl/components/page-setup';
import { concat } from '@ember/helper';
import filterSort from 'bcl/helpers/filter-sort';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import notEq from 'ember-truth-helpers/helpers/not-eq';
import preventUnload from 'bcl/helpers/prevent-unload';
import type RouterService from '@ember/routing/router-service';
import type StocktakingService from 'bcl/services/stocktaking';
import type LocationModel from 'bcl/models/location';
import type StocktakingModel from 'bcl/models/stocktaking';
import { action } from '@ember/object';
import type StocktakingEntryModel from 'bcl/models/stocktaking-entry';

export interface StocktakingRouteSignature {
  // The arguments accepted by the component
  Args: {
    model: {
      allLocations: LocationModel[];
      stocktaking: StocktakingModel;
    };
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}
export default class StocktakingRoute extends Component<StocktakingRouteSignature> {
  @service declare router: RouterService;
  @service declare stocktaking: StocktakingService;
  @service declare store: Store;

  get missingLocations() {
    return this.args.model.stocktaking.stocktakingEntries
      .filter((ste: StocktakingEntryModel) => !ste.hasSetAmount)
      .map((ste: StocktakingEntryModel) => ste.location_item.location.name)
      .reduce((acc, cur) => {
        if (!acc.includes(cur)) {
          acc.push(cur);
        }
        return acc;
      }, [] as string[]);
  }

  @action async delete() {
    if (confirm('Soll die aktuelle Inventur gelöscht werden?')) {
      this.args.model.stocktaking.status = 'canceled';
      await this.args.model.stocktaking.save();
      this.router.transitionTo('auth.location');
    }
  }
  @tracked isSaving = false;
  get allowUnload() {
    const changes =
      this.args.model.stocktaking?.stocktakingEntries.filter(
        (ste: StocktakingEntryModel) =>
          ste.hasDirtyAttributes && ste.hasSetAmount
      ) ?? [];
    return !changes.length;
  }
  @action async accept() {
    this.isSaving = true;
    // check if we have a connection
    if (!(await this.stocktaking.testConnection())) {
      this.isSaving = false;
      alert('Keine Verbindung zum Server. Bitte versuche es erneut.');
      return;
    }

    await this.stocktaking.waitForAllSaved();
    this.args.model.stocktaking.status = 'done';
    await this.args.model.stocktaking.save();

    this.router.transitionTo('auth.location');
    this.isSaving = false;
  }
  @action async save() {
    await this.stocktaking.waitForAllSaved();

    this.router.transitionTo('auth');
  }
  <template>
    {{#unless this.allowUnload}}
      {{preventUnload}}
    {{/unless}}
    {{pageTitle (concat "Inventur für " this.router.currentRoute.params.name)}}
    <PageSetup @title={{concat "Inventur für "}}>
      <:header>
        <h1 class="text-2xl inline font-bold leading-6 text-gray-900">
          {{this.router.currentRoute.params.name}}
        </h1>
        <div class="inline ml-8">

          {{#each
            (filterSort @model.allLocations "active" "name")
            as |location|
          }}
            {{#if (notEq this.router.currentRoute.params.name location.name)}}
              <LinkTo
                @route="auth.stocktaking.name"
                @model={{location.name}}
                class="inline-block ml-2 rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >wechsel zu {{location.name}}</LinkTo>
            {{/if}}
          {{/each}}
        </div>

      </:header>
      <:default>

        {{outlet}}
        <div class="mt-6 flex items-center justify-end gap-x-6">
          <button
            {{on "click" this.delete}}
            type="button"
            class="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >löschen</button>
          <button
            {{on "click" this.save}}
            type="submit"
            class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >zwischenspeichern</button>
          {{#if this.missingLocations.length}}
            Es fehlen noch:
            {{this.missingLocations}}
            <button
              disabled
              type="submit"
              class="rounded-md bg-gray-300 px-3 py-2 text-sm font-semibold text-white shadow-sm cursor-not-allowed"
            >annehmen</button>
          {{else if this.stocktaking.stillToSave}}
            Es werden noch
            {{this.stocktaking.stillToSave}}
            Einträge gespeichert
          {{else}}
            {{#if this.isSaving}}
              <button
                disabled
                type="submit"
                class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              >speichere ...</button>
            {{else}}
              <button
                {{on "click" this.accept}}
                type="submit"
                class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >annehmen</button>
            {{/if}}
          {{/if}}
        </div>
      </:default>
    </PageSetup>
  </template>
}
